// ---------------------------------------------------------------------------
// S18. The handset's queue, in IndexedDB: offline.ts's CaptureStore, for real.
//
// IndexedDB and not localStorage because offline.ts's header asks for a store
// that survives the browser being killed and the handset restarting, and because
// a queue is a set of records keyed by id, which is what an object store is.
//
// NULL WHERE THERE IS NO INDEXEDDB (a private window that refuses it, an old
// browser). The caller then says the check-in could not be recorded, rather than
// claiming it was saved somewhere that does not exist.
// ---------------------------------------------------------------------------

import type { CaptureStore, QueuedCapture } from "./offline";

const DB = "verbunk-offline";
const STORE = "captures";

function request<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

function open(): Promise<IDBDatabase> {
  const r = indexedDB.open(DB, 1);
  r.onupgradeneeded = () => {
    if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE, { keyPath: "id" });
  };
  return request(r);
}

async function withStore<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => Promise<T>): Promise<T> {
  const db = await open();
  try {
    return await fn(db.transaction(STORE, mode).objectStore(STORE));
  } finally {
    db.close();
  }
}

export function captureStore(): CaptureStore | null {
  if (typeof indexedDB === "undefined") return null;
  return {
    all: () => withStore("readonly", (s) => request(s.getAll() as IDBRequest<QueuedCapture[]>)),
    put: (capture) => withStore("readwrite", async (s) => void (await request(s.put(capture)))),
    remove: (ids) =>
      withStore("readwrite", async (s) => {
        for (const id of ids) await request(s.delete(id));
      }),
  };
}
